/* eslint-disable react/jsx-props-no-spreading */
import { MD5 } from 'crypto-js';
import dayjs from 'dayjs';
import { useEffect, useRef, useState } from 'react';
import Flatpickr from 'react-flatpickr';
import { actionIcons } from '../../configs/Icons';
import { ApiAdminSearch } from '../app/Admins/AdminsPageOperation';
import { useTemplate } from '../features/template/TemplateProvider';
import AxiosAPI from './AxiosAPI';
import cn from './cn';
import { evaluate } from './Math';
import { PasswordGenerator } from './PasswordGenerator';
import { CrossIcon, EyeIcon, EyeIconHide, ImageUploadingIcon, ReGenerateIcon } from './SVGIcon';

export const ApiCustomerSearch = (q) =>
    new Promise((resolve, reject) => {
        AxiosAPI()
            .setPath(`/api/customers/search`, { q })
            .get()
            .then(({ data }) => {
                if (data.status === 0) {
                    resolve(data);
                } else {
                    reject(data.title);
                }
            })
            .catch((error) => {
                reject(error);
            });
    });

function OnSearching({ loading, handleClearUser }) {
    if (loading) {
        return (
            <button
                type="button"
                onClick={() => handleClearUser()}
                className="size-8"
                aria-label="Loading..."
            >
                <ImageUploadingIcon />
            </button>
        );
    }
    return (
        <button
            type="button"
            onClick={() => handleClearUser()}
            className="size-6 text-error"
            aria-label="Loading..."
        >
            <CrossIcon />
        </button>
    );
}

export const addOption = (id, title, value) => ({ id, title, value });

export function TextareaField({
    label,
    name,
    onChange,
    formData,
    placeholder = '',
    height = 'h-full',
    className = '',
    optional = false,
    disabled = false,
    inputRef,
    required = false,
    rest
}) {
    return (
        <div className={cn('w-full flex flex-col custom-field', className)}>
            {!!label?.length && (
                <label className="pl-1 mb-1 text-sm md:text-base capitalize h-auto w-full font-normal text-left">
                    {label}{' '}
                    {optional && required === false && (
                        <span className="text-primary-700 text-sm"> (optional)</span>
                    )}
                    {required && optional === false && (
                        <span className="text-error text-base">*</span>
                    )}
                </label>
            )}
            <textarea
                type="text"
                ref={inputRef}
                name={name}
                value={formData[name] || ''}
                onChange={onChange}
                placeholder={placeholder || label}
                cols={2}
                disabled={disabled}
                autoComplete="off"
                className={cn(
                    `flex-shrink w-full bg-transparent rounded-lg block outline-none border border-primary-200 px-2 text-primary text-xs md:text-sm leading-6 font-normal disabled:bg-primary-50 disabled:text-primary-700 pt-1.5`,
                    height
                )}
                {...rest}
            />
        </div>
    );
}
export function InputFieldSearch({
    name,
    label,
    onChange,
    onKeyUp,
    formData,
    placeholder = '',
    className = ''
}) {
    return (
        <div
            className={cn(
                `h-10 w-full flex flex-row gap-x-2 items-center justify-between px-1 md:px-3 rounded-lg border border- overflow-hidden`,
                className
            )}
        >
            <input
                type="text"
                name={name}
                value={formData[name]}
                onChange={onChange}
                onKeyUp={onKeyUp}
                placeholder={placeholder || label}
                autoComplete="off"
                className="flex-shrink h-full w-full block outline-none border-none text-primary text-xs md:text-sm leading-6 font-normal"
            />
            <button type="button" className="min-h-4 min-w-4" aria-label="Loading...">
                <img src={actionIcons.searchW} alt="" className="w-full h-full" />
            </button>
        </div>
    );
}
export function InputNumberField({
    label = '',
    name,
    onChange,
    formData,
    placeholder = '',
    height = 'h-[68px]',
    width = 'w-full',
    className = '',
    optional = false,
    disabled = false,
    readOnly = false,
    required = false,
    type = 'text'
}) {
    const modifyOnChange = (ev) => {
        const outputObj = {
            target: {
                name: ev.target.name,
                value: evaluate(ev.target.value)
            }
        };

        onChange(outputObj);
    };
    return (
        <div className={`${height} ${width} flex flex-col custom-field`}>
            {!!label?.length && (
                <label className="pl-2 mb-1 text-base capitalize h-auto w-full font-normal">
                    {label}{' '}
                    {optional && <span className="text-primary-700 text-sm"> (optional)</span>}
                    {required && optional === false && (
                        <span className="text-error text-base">*</span>
                    )}
                </label>
            )}
            <input
                type={type}
                name={name}
                value={formData[name] || ''}
                onChange={modifyOnChange}
                placeholder={placeholder || label}
                disabled={disabled}
                readOnly={readOnly}
                autoComplete="off"
                className={cn(
                    `flex-shrink h-full w-full bg-transparent rounded-lg block outline-none border border-primary-200 px-2 md:px-3 text-primary text-sm leading-6 font-normal disabled:bg-primary-50 disabled:text-primary-700 read-only:border-primary-100 read-only:bg-primary-50/50 read-only:text-primary-700 read-only:cursor-default`,
                    className
                )}
            />
        </div>
    );
}

export function SelectOption({
    label,
    name,
    options,
    onChange,
    formData,
    placeholder = '',
    className = '',
    height = '',
    fontWeight = 'font-normal',
    optional = false,
    disabled = false,
    required = false
}) {
    return (
        <div className={cn('flex flex-col w-full custom-field', className)}>
            {!!label?.length && (
                <label
                    className={`pl-1 mb-1 text-sm md:text-base capitalize w-full text-primary text-left ${fontWeight} `}
                >
                    {label}{' '}
                    {optional && required === false && (
                        <span className="text-primary-700 text-sm"> (optional)</span>
                    )}
                    {required && optional === false && (
                        <span className="text-error text-base">*</span>
                    )}
                </label>
            )}
            <select
                onChange={onChange}
                name={name}
                value={formData[name] || ''}
                className={cn(
                    `dropdown w-full h-8.5 md:h-10 bg-transparent rounded-lg outline-none border px-2 text-primary text-xs md:text-sm leading-6 font-normal capitalize disabled:bg-primary-50`,
                    height
                )}
                disabled={disabled}
            >
                <option value="">Select {placeholder || label} </option>
                {options?.map((option) => (
                    <option key={option?.id} value={option?.value} className="capitalize">
                        {option?.title}
                    </option>
                ))}
            </select>
        </div>
    );
}

export function InputDateField({
    label,
    name,
    onChange: evOnChange,
    formData,
    className = '',
    optional = false,
    required = false,
    minDate = '',
    maxDate = ''
}) {
    const handelOnChange = ([date]) => {
        const ev = {
            target: {
                name,
                value: date ? dayjs(date).format('YYYY-MM-DD') : ''
            }
        };
        evOnChange(ev);
    };
    const value = () => {
        if (formData[name] && typeof formData[name] === 'number') {
            return dayjs(formData[name] * 1000)?.format('YYYY-MM-DD');
        }
        return dayjs(formData[name]).format('YYYY-MM-DD');
    };

    return (
        <div className="h-full w-full flex flex-col">
            {!!label?.length && (
                <label
                    htmlFor={`input-date-${formData[name]}`}
                    className="pl-1 mb-1 text-base capitalize text-primary h-auto w-full font-normal"
                >
                    {label}
                    {optional && required === false && (
                        <span className="text-primary-700 text-sm"> (optional)</span>
                    )}
                    {required && optional === false && (
                        <span className="text-error text-base"> *</span>
                    )}
                </label>
            )}

            <Flatpickr
                name={name}
                id={`input-date-${formData[name]}`}
                options={{ dateFormat: 'Y-m-d', minDate, maxDate }}
                value={value()}
                onChange={handelOnChange}
                readOnly={false}
                placeholder="Select Date"
                className={`flex-shrink w-full  bg-transparent rounded-lg block outline-none border border-neutral-900 border-opacity-20 px-2 md:px-3 text-primary text-sm leading-6 font-normal h-10 ${className}`}
            />
        </div>
    );
}

export function InputCheckbox({
    name,
    text,
    html,
    onChange,
    formData,
    className = '',
    disabled = false
}) {
    if (!text?.length && !html) {
        return null;
    }
    return (
        <label
            className={cn(
                'text-base text-primary-700 relative font-medium select-none inline-flex justify-start items-start gap-1.5',
                className
            )}
            htmlFor={name}
        >
            <input
                type="checkbox"
                name={name}
                value={formData[name] || ''}
                onChange={(e) => onChange(e)}
                disabled={disabled}
                checked={formData[name] || false}
                id={name}
                className="w-4.5 h-4.5 mt-0.5 appearance-none shrink-0 checked:bg-secondary checked:border-0 rounded-[5px] border border-primary-200 cursor-pointer"
            />
            <svg
                className="absolute cursor-pointer peer-checked:block w-2.5 h-2.5 top-1.5 left-1"
                viewBox="0 0 10 10"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <g clipPath="url(#clip0_1262_530)">
                    <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M0.972811 6.39607L3.04424 8.82857C3.17388 8.98392 3.34531 9.10035 3.53745 9.16321C3.82138 9.25249 4.21424 9.24642 4.55138 8.76535L9.17817 1.55142C9.21245 1.49821 9.23888 1.43999 9.25638 1.37999C9.32495 1.14428 9.31031 0.62678 8.79674 0.833566C8.65424 0.89178 8.52817 0.985352 8.43138 1.10464L4.07067 6.3525C3.90353 6.55392 3.60495 6.58142 3.40317 6.41428C3.38424 6.39892 3.36638 6.38178 3.34995 6.36285L2.37638 5.25642C2.17924 5.02642 1.89674 4.88678 1.59424 4.86785C1.26745 4.85428 0.889954 4.95035 0.751739 5.42714C0.658882 5.76749 0.741382 6.12999 0.972811 6.39607Z"
                        fill="#F9F9F9"
                    />
                </g>
                <defs>
                    <clipPath id="clip0_1262_530">
                        <rect width="10" height="10" fill="white" />
                    </clipPath>
                </defs>
            </svg>
            <div className="cursor-pointer">
                {text?.length ? (
                    <span className="text-primary font-normal text-sm md:text-base leading-[18px]">
                        {text}
                    </span>
                ) : (
                    html
                )}
            </div>
        </label>
    );
}

export function InputPassword({
    label = '',
    name,
    onChange,
    formData,
    placeholder = '',
    className = '',
    height = '',
    passGenerate = false,
    optional = false,
    required = false,
    disabled = false
}) {
    const [showPassword, setShowPassword] = useState(false);

    return (
        <div className={cn('w-full flex flex-col custom-field', className)}>
            {!!label?.length && (
                <label className="pl-1 text-sm md:text-base capitalize h-auto w-full font-normal">
                    {label}
                    {optional && required === false && (
                        <span className="text-primary-700 text-sm"> (optional)</span>
                    )}
                    {required && optional === false && (
                        <span className="text-error text-base">*</span>
                    )}
                </label>
            )}
            <div className="relative h-full w-full custom-field">
                <input
                    type={showPassword ? 'text' : 'password'}
                    name={name}
                    value={formData[name] || ''}
                    onChange={onChange}
                    placeholder={placeholder || label}
                    disabled={disabled}
                    autoComplete="off"
                    className={cn(
                        `flex-shrink w-full h-8.5 md:h-10 bg-transparent rounded-lg block outline-none border border-primary-200 px-2 text-primary text-xs md:text-sm leading-6 font-normal disabled:bg-primary-50 disabled:text-primary-700`,
                        height
                    )}
                />
                {passGenerate ? (
                    <button
                        type="button"
                        onClick={() => {
                            const randPass = PasswordGenerator(8);
                            // Copy to clipboard
                            navigator?.clipboard?.writeText(randPass);

                            // Trigger onChange
                            onChange({
                                target: {
                                    name,
                                    value: randPass
                                }
                            });
                        }}
                        className="absolute h-4 w-4 top-1/2 right-8 transform -translate-y-1/2 text-primary-700 select-none outline-none focus:outline-none"
                    >
                        <ReGenerateIcon className="w-full h-full" />
                    </button>
                ) : null}
                <button
                    type="button"
                    onClick={() => setShowPassword((cuData) => !cuData)}
                    className="absolute h-4 w-4 top-1/2 right-2 transform -translate-y-1/2 text-primary-700 select-none outline-none focus:outline-none"
                >
                    {showPassword ? <EyeIcon /> : <EyeIconHide />}
                </button>
            </div>
        </div>
    );
}
export function InputWithCustomerSearch({
    name,
    formData,
    label,
    placeholder,
    onChange,
    className,
    required = false
}) {
    const [customerData, setCustomerData] = useState([]);
    const { setMessage } = useTemplate();
    const ref = useRef(null);
    const [searchText, setSearchText] = useState(
        customerData.find((i) => i.id === formData[name])?.organization || ''
    );
    const [isShowSearch, setIsShowSearch] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleSearchText = (e) => {
        const { value } = e.target;
        setSearchText(value);
    };
    const handleClearUser = () => {
        if (isShowSearch) setIsShowSearch(false);
        setCustomerData([]);
        setSearchText('');
        onChange({
            target: {
                name,
                value: 0
            }
        });
    };

    const handleSelectCustomer = (customer) => {
        onChange({
            target: {
                name,
                value: customer.id
            }
        });
        setIsShowSearch(false);
        setSearchText(customer?.organization);
    };
    // outside click
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (ref.current && !ref.current.contains(event.target)) {
                setIsShowSearch(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        if (searchText.length > 2) {
            setLoading(true);
            ApiCustomerSearch(searchText)
                .then(({ data }) => {
                    setLoading(false);
                    setCustomerData(data.customers);
                })
                .catch((err) => {
                    setLoading(false);
                    setMessage({ type: 'error', text: err.toString(0) });
                });
        }
    }, [searchText, setMessage]);

    return (
        <div className="flex flex-col" ref={ref}>
            <div className="flex flex-col">
                <label className="pl-2 text-base capitalize h-auto w-full font-normal">
                    {label}
                    {required && <span className="text-error"> *</span>}
                </label>
                <div
                    className={cn(
                        'flex flex-row gap-x-2 items-center px-1 md:px-3 rounded-lg border border-neutral-900 border-opacity-20 overflow-hidden h-10 [&:has(.error)]:border-error',
                        className
                    )}
                >
                    <input
                        type="text"
                        name={name}
                        value={searchText}
                        onChange={(e) => handleSearchText(e)}
                        onClick={() => setIsShowSearch(true)}
                        placeholder={placeholder || label}
                        autoComplete="off"
                        className="flex-shrink h-full w-full block outline-none border-none text-primary text-sm md:text-sm leading-6 font-normal"
                    />
                    {!isShowSearch && searchText.length === 0 ? (
                        <button
                            type="button"
                            className="size-4 "
                            onClick={() => setIsShowSearch((cuData) => !cuData)}
                            aria-label="Loading..."
                        >
                            <img src={actionIcons.searchW} alt="" className="w-full h-full" />
                        </button>
                    ) : (
                        <OnSearching loading={loading} handleClearUser={handleClearUser} />
                    )}
                </div>
            </div>
            {isShowSearch && searchText.length > 2 && (
                <div className="relative">
                    <div className="absolute top-full w-full bg-white border border-primary-200 rounded-lg shadow-lg z-10 overflow-hidden max-h-80 scrollbar overflow-y-auto">
                        {customerData?.length > 0 ? (
                            customerData.map((customer, index) => (
                                <button
                                    type="button"
                                    key={MD5(customer.organization + index)}
                                    onClick={() => handleSelectCustomer(customer)}
                                    className="flex flex-row items-center px-3 py-2 hover:bg-secondary-50 w-full"
                                >
                                    <span className="text-primary-700 text-sm ">
                                        {customer.organization}
                                    </span>
                                </button>
                            ))
                        ) : (
                            <span className="text-primary-700 text-sm font-normal px-3 py-2 h-10 block">
                                No Customer Found
                            </span>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
export function InputWithAdminSearch({
    name,
    formData,
    label,
    placeholder,
    onChange,
    className,
    required = false
}) {
    const [adminData, setAdminData] = useState([]);
    const { setMessage } = useTemplate();
    const ref = useRef(null);
    const [searchText, setSearchText] = useState(
        adminData?.find((i) => i.id === formData[name])?.fullName || ''
    );
    const [isShowSearch, setIsShowSearch] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleSearchText = (e) => {
        const { value } = e.target;
        setSearchText(value.trimStart());
    };
    const handleClearUser = () => {
        if (isShowSearch) {
            setIsShowSearch(false);
        }
        setAdminData([]);
        setSearchText('');
    };

    const handleSelectCustomer = (admin) => {
        onChange({
            target: {
                name,
                value: admin
            }
        });
        setIsShowSearch(false);
        setSearchText('');
    };
    // outside click
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (ref.current && !ref.current.contains(event.target)) {
                setIsShowSearch(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        if (searchText.length > 2) {
            setLoading(true);
            ApiAdminSearch(searchText)
                .then(({ data }) => {
                    setLoading(false);
                    setAdminData(data.users);
                })
                .catch((err) => {
                    setLoading(false);
                    setMessage({ type: 'error', text: err.toString(0) });
                });
        }
    }, [searchText, setMessage]);

    return (
        <div className="flex flex-col" ref={ref}>
            <div className="flex flex-col gap-0.5">
                <label className="pl-2 text-base capitalize h-auto w-full font-normal text-start">
                    {label}
                    {required && <span className="text-error"> *</span>}
                </label>
                <div
                    className={cn(
                        'flex flex-row gap-x-2 items-center px-1 md:px-3 rounded-lg border border-neutral-900 border-opacity-20 overflow-hidden h-10 [&:has(.error)]:border-error',
                        className
                    )}
                >
                    <input
                        type="text"
                        name={name}
                        value={searchText}
                        onChange={(e) => handleSearchText(e)}
                        onClick={() => setIsShowSearch(true)}
                        placeholder={placeholder || label}
                        autoComplete="off"
                        className="flex-shrink h-full w-full block outline-none border-none text-primary text-sm md:text-sm leading-6 font-normal"
                    />
                    {!isShowSearch && searchText.length === 0 ? (
                        <button
                            type="button"
                            className="size-4 "
                            onClick={() => setIsShowSearch((cuData) => !cuData)}
                            aria-label="Loading..."
                        >
                            <img src={actionIcons.searchW} alt="" className="w-full h-full" />
                        </button>
                    ) : (
                        <OnSearching loading={loading} handleClearUser={handleClearUser} />
                    )}
                </div>
            </div>
            {isShowSearch && searchText.length > 2 && (
                <div className="relative">
                    <div className="absolute top-full w-full bg-white border border-primary-200 rounded-lg shadow-lg z-10 overflow-hidden max-h-80 scrollbar overflow-y-auto">
                        {adminData?.length > 0 ? (
                            adminData.map((admin, index) => (
                                <button
                                    type="button"
                                    key={MD5(admin.organization + index)}
                                    onClick={() => handleSelectCustomer(admin)}
                                    className="flex flex-row items-center px-3 py-2 hover:bg-secondary-50 w-full"
                                >
                                    <span className="text-primary-700 text-sm ">
                                        {admin.fullName}
                                    </span>
                                </button>
                            ))
                        ) : (
                            <span className="text-primary-700 text-sm font-normal px-3 py-2 h-10 block">
                                No User Found!
                            </span>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

function InputField({
    label = '',
    name,
    onChange,
    formData,
    placeholder = '',
    className = '',
    optional = false,
    disabled = false,
    readOnly = false,
    type = 'text',
    required = false,
    height = '',
    ...rest
}) {
    return (
        <div className={cn('flex flex-col w-full custom-field', className)}>
            {!!label?.length && (
                <label className="pl-1 mb-1 text-sm md:text-base capitalize w-full font-normal text-left">
                    {label}{' '}
                    {optional && required === false && (
                        <span className="text-primary-700 text-sm"> (optional)</span>
                    )}
                    {required && optional === false && (
                        <span className="text-error text-base">*</span>
                    )}
                </label>
            )}
            <input
                type={type}
                name={name}
                value={formData[name] || ''}
                onChange={onChange}
                placeholder={placeholder || label}
                disabled={disabled}
                readOnly={readOnly}
                autoComplete="off"
                className={cn(
                    `flex-shrink w-full h-8.5 md:h-10 bg-transparent rounded-lg block outline-none border border-primary-200 px-2 text-primary text-xs md:text-sm leading-6 font-normal disabled:bg-primary-50 disabled:text-primary-700 read-only:border-primary-100 read-only:bg-primary-50/50 read-only:text-primary-700 read-only:cursor-default`,
                    height
                )}
                {...rest}
            />
        </div>
    );
}

export default InputField;
